<template>
  <a-modal
    :visible="visible"
    title="编辑患者信息"
    :maskClosable="false"
    :destroyOnClose="true"
    width="630px"
    :style="{ top: '40px' }"
    :bodyStyle="{ height: '700px', padding: '0px' }"
    :confirmLoading="saving"
    ok-text="保存"
    cancel-text="取消"
    @cancel="onCancel"
    @ok="onSave"
    class="edit-portrait-modal"
  >
    <div class="edit-portrait-modal__body">
      <a-tabs v-model:activeKey="activeKey" class="edit-portrait-modal__tabs">
        <a-tab-pane key="basic" tab="基本信息" />
        <a-tab-pane key="summary" tab="患者摘要" />
        <a-tab-pane key="portrait" tab="患者画像" />
      </a-tabs>

      <BasicInfoTab
        v-if="activeKey === 'basic'"
        :form="form"
        :marriage-options="marriageOptions"
        :compliance-options="complianceOptions"
        :diagnosis-default-tags="diagnosisDefaultTags"
        @toggle-tag="toggleTag"
        @update-field="updateBasicField"
      />

      <SummaryTab v-if="activeKey === 'summary'" :form="form" @update-field="updateSummaryField" />

      <PortraitTab
        v-if="activeKey === 'portrait'"
        :form="form"
        :emotion-options="emotionOptions"
        :response-options="responseOptions"
        :contact-options="contactOptions"
        :cooperation-options="cooperationOptions"
        @update-field="updatePortraitField"
      />
    </div>
  </a-modal>
</template>

<script setup>
import { defineEmits, defineProps, ref, watch } from 'vue'
import { message } from 'ant-design-vue'
import { modifyPatientPortrait } from '@/api/patientFile'
import BasicInfoTab from './EditPortraitModal/BasicInfoTab.vue'
import SummaryTab from './EditPortraitModal/SummaryTab.vue'
import PortraitTab from './EditPortraitModal/PortraitTab.vue'
import { useEditPortraitForm } from '../hooks/useEditPortraitForm'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  initialData: {
    type: Object,
    default: () => ({})
  },
  portraitDict: {
    type: [Array, Object],
    default: () => []
  }
})

const emit = defineEmits(['update:visible', 'save'])

const activeKey = ref('basic')
const saving = ref(false)
const {
  form,
  marriageOptions,
  complianceOptions,
  diagnosisDefaultTags,
  emotionOptions,
  responseOptions,
  contactOptions,
  cooperationOptions,
  syncForm,
  createModifyPayload,
  toggleDiagnoseTag,
  setDiagnoseRemark
} = useEditPortraitForm(() => props.portraitDict)

watch(
  () => props.visible,
  show => {
    if (!show) return
    // 每次重新打开都以最新详情重置表单，避免保留上一次未提交内容。
    activeKey.value = 'basic'
    syncForm(props.initialData || {})
  },
  { immediate: true }
)

const toggleTag = tag => toggleDiagnoseTag(tag)

const updateBasicField = ({ key, value }) => {
  if (!key) return
  if (key === 'diagnosisManualTags') {
    setDiagnoseRemark(value)
    return
  }
  form.basic[key] = value
}

const updateSummaryField = ({ key, value }) => {
  if (!key) return
  form.summary[key] = value
}

const updatePortraitField = ({ key, value }) => {
  if (!key) return
  form.portrait[key] = value
}

const onCancel = () => {
  emit('update:visible', false)
}

const onSave = async () => {
  if (saving.value) return
  // modify 接口只提交弹窗内可编辑字段；只读基础信息不回传，避免覆盖账号绑定信息。
  const payload = createModifyPayload(props.initialData || {})
  if (!payload.ucDoctorId || !payload.ucPatientId) {
    message.error('缺少医患信息，无法保存患者画像')
    return
  }
  saving.value = true
  try {
    await modifyPatientPortrait(payload)
    emit('save')
    emit('update:visible', false)
  } finally {
    saving.value = false
  }
}
</script>

<style lang="scss" scoped>
.edit-portrait-modal {
  .edit-portrait-modal__body {
    padding: 0 20px 18px;
    height: 100%;
    min-height: 0;
    overflow: auto;
  }
  :deep(.ant-modal-body) {
    padding: 0;
  }

  .edit-portrait-modal__tabs {
    margin: 0 -20px;

    :deep(.ant-tabs-nav) {
      margin: 0;
    }

    :deep(.ant-tabs-nav-wrap) {
      width: 100%;
    }

    :deep(.ant-tabs-nav-list) {
      width: 100%;
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    :deep(.ant-tabs-bar) {
      margin-bottom: 0;
      border-bottom: 1px solid #e6ebf2;
    }

    :deep(.ant-tabs-tab) {
      margin: 0;
      min-width: 0;
      width: 210px;
      text-align: center;
      color: #333333;
      font-size: 18px;
      font-weight: 400;
      line-height: 22px;
    }

    :deep(.ant-tabs-tab-active) {
      font-weight: 600;
      color: #1890ff;
    }

    :deep(.ant-tabs-tab-btn) {
      font-size: 16px;
    }

    :deep(.ant-tabs-tab-active .ant-tabs-tab-btn) {
      color: #1677ff;
    }

    :deep(.ant-tabs-ink-bar) {
      height: 2px;
      border-radius: 2px;
      background: #1677ff;
    }
  }
}
</style>
